import React from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { Button } from './ui/button';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';
import { DataTableSelectAllCheckbox } from './DataTableSelectAllCheckbox';
import { RotatingTrashIcon } from './RotatingTrashIcon';
import { SlidingFolderInputIcon } from './SlidingFolderInputIcon';
import { notesSelectionColumnClassName } from './NotesListLayout';

interface NotesBulkActionsProps {
  selectedCount: number;
  totalCount: number;
  disabled?: boolean;
  onSelectAllChange: (checked: boolean) => void;
  onMoveToProject: () => void;
  onMoveToTrash: () => void;
}

export const NotesBulkActions: React.FC<NotesBulkActionsProps> = ({
  selectedCount,
  totalCount,
  disabled = false,
  onSelectAllChange,
  onMoveToProject,
  onMoveToTrash,
}) => {
  const { t } = useLocale();
  const actionsDisabled = disabled || selectedCount <= 0;
  const selectAllChecked =
    totalCount > 0 && selectedCount === totalCount
      ? true
      : selectedCount > 0
        ? 'indeterminate'
        : false;

  return (
    <TooltipProvider>
      <div className="flex min-w-0 items-center gap-3">
        <div className={notesSelectionColumnClassName}>
          <DataTableSelectAllCheckbox
            checked={selectAllChecked}
            onCheckedChange={onSelectAllChange}
            label={t('notes_select_all')}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2">
          <ButtonTooltip tooltip={t('move_selected_notes_to_project_tooltip')} side="bottom">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={actionsDisabled}
              onClick={onMoveToProject}
              className="group inline-flex shrink-0 items-center gap-1.5"
            >
              <SlidingFolderInputIcon />
              {t('move_to_project')}
            </Button>
          </ButtonTooltip>
          <ButtonTooltip tooltip={t('move_selected_notes_to_trash_tooltip')} side="bottom">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={actionsDisabled}
              onClick={onMoveToTrash}
              className="group h-8 w-8 shrink-0 p-0"
              aria-label={t('move_to_trash')}
            >
              <RotatingTrashIcon />
            </Button>
          </ButtonTooltip>
          {selectedCount > 0 ? (
            <span className="min-w-0 truncate text-sm text-muted-foreground">
              {t('notes_selected_count').replace('{count}', String(selectedCount))}
            </span>
          ) : null}
        </div>
      </div>
    </TooltipProvider>
  );
};
